import React, { useCallback } from 'react';
import { Paper, TableRow, Box, Button, Typography } from '@material-ui/core';
import { CopyTextButton } from '@lib/common';
import { PropertyDevice } from 'app/state';
import {
  StyledTable,
  StyledTableBody,
  LabelCell,
  ValueCell,
  SectionHeaderRow,
} from './styled-table';
import { renderValue } from './device-detail-tabs-helpers';
import { ThingType } from '@lib/state/api/generated/access-control/models/thing-type';
import { DeviceDeleteModal } from '../device-delete-modal';

interface DeviceDetailsTabProps {
  device: PropertyDevice;
  paperClassName: string;
  onIdentifyDevice: (device: PropertyDevice) => void;
  onDeleteDevice: (thingName: string) => void;
  onDeleteSuccess: () => void;
}

export function DeviceDetailsTab({
  device,
  paperClassName,
  onIdentifyDevice,
  onDeleteDevice,
  onDeleteSuccess,
}: DeviceDetailsTabProps) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const onConfirmDelete = useCallback(() => {
    if (device.thingName) onDeleteDevice(device.thingName);
  }, [onDeleteDevice, device.thingName]);

  const onCancelDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const onDeleteClick = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const onSuccess = useCallback(() => {
    setConfirmDeleteOpen(false);
    onDeleteSuccess();
  }, [onDeleteSuccess]);

  return (
    <Paper elevation={4} className={paperClassName}>
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          Device Information
        </Typography>
        <StyledTable>
          <StyledTableBody>
            <SectionHeaderRow>Identification</SectionHeaderRow>
            <TableRow hover>
              <LabelCell>Device Identifier</LabelCell>
              <ValueCell>
                {renderValue('thingIdentifier', device.thingIdentifier, device)}
                {device.thingName && (
                  <CopyTextButton copyText={device.thingName} tooltipText="Copy Device ID" />
                )}
              </ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell>Device Type</LabelCell>
              <ValueCell>{renderValue('thingType', device.thingType, device)}</ValueCell>
            </TableRow>
            <SectionHeaderRow>Location</SectionHeaderRow>
            <TableRow hover>
              <LabelCell>Location Type</LabelCell>
              <ValueCell>
                {renderValue('thingLocationType', device.thingLocationType, device)}
              </ValueCell>
            </TableRow>
            <SectionHeaderRow>Connectivity</SectionHeaderRow>
            <TableRow hover>
              <LabelCell>Access Point</LabelCell>
              <ValueCell>{renderValue('accessPoint', device.accessPoint, device)}</ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell>MAC Address</LabelCell>
              <ValueCell>{renderValue('macAddress', device.macAddress, device)}</ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell>Version</LabelCell>
              <ValueCell>{renderValue('version', device.version, device)}</ValueCell>
            </TableRow>
            <SectionHeaderRow>Status</SectionHeaderRow>
            <TableRow hover>
              <LabelCell>Power Status</LabelCell>
              <ValueCell>{renderValue('powerStatus', device.powerStatus, device)}</ValueCell>
            </TableRow>
            <SectionHeaderRow>Battery</SectionHeaderRow>
            <TableRow hover>
              <LabelCell>Battery Level</LabelCell>
              <ValueCell>
                {device.batteryLevelDescription ? (
                  <Typography variant="body2" color="textSecondary">
                    {device.batteryLevelDescription}
                  </Typography>
                ) : (
                  renderValue('batteryLevel', device.batteryLevel, device)
                )}
              </ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell>Battery Status</LabelCell>
              <ValueCell>{renderValue('batteryStatus', device.batteryStatus, device)}</ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell>Last Battery Low</LabelCell>
              <ValueCell>{renderValue('lastBatteryLow', device.lastBatteryLow, device)}</ValueCell>
            </TableRow>
            <SectionHeaderRow>Activity</SectionHeaderRow>
            <TableRow hover>
              <LabelCell>Last Seen</LabelCell>
              <ValueCell>{renderValue('lastSeen', device.lastSeen, device)}</ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell>Last Offline</LabelCell>
              <ValueCell>{renderValue('lastOffline', device.lastOffline, device)}</ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell>Last Online</LabelCell>
              <ValueCell>{renderValue('lastOnline', device.lastOnline, device)}</ValueCell>
            </TableRow>
            <TableRow hover>
              <LabelCell isLastRow>Last Power Loss</LabelCell>
              <ValueCell isLastRow>
                {renderValue('lastPowerLoss', device.lastPowerLoss, device)}
              </ValueCell>
            </TableRow>
            <SectionHeaderRow>Actions</SectionHeaderRow>
            <TableRow hover>
              <LabelCell isLastRow>Device Actions</LabelCell>
              <ValueCell isLastRow>
                {device.thingType !== ThingType.WaterFlowSensor && (
                  <Button
                    size="small"
                    type="button"
                    variant="contained"
                    color="default"
                    onClick={() => onIdentifyDevice(device)}
                  >
                    Identify
                  </Button>
                )}
                <Button
                  size="small"
                  type="button"
                  variant="contained"
                  color="default"
                  onClick={onDeleteClick}
                  style={{ marginLeft: '1em' }}
                >
                  Delete
                </Button>
              </ValueCell>
            </TableRow>
          </StyledTableBody>
        </StyledTable>
      </Box>
      <DeviceDeleteModal
        open={confirmDeleteOpen}
        onClose={onCancelDelete}
        onConfirm={onConfirmDelete}
        onSuccess={onSuccess}
      />
    </Paper>
  );
}
